/**
 * Filter Results Service
 *
 * Pure matching rules that turn a `searchParameters`-shaped object (see redux/STORE_DESIGN.md#state-shape)
 * into the house plans / housing units the listing sheet shows. Parameterized on the parameters
 * object instead of reading the store, so the same rules run against both the applied
 * `searchParameters` (redux/selectors.js#selectFilteredHousePlans/#selectFilteredHousingUnits —
 * what the panel and map actually show) and the Filters modal's pending `filterDraft`
 * (redux/selectors.js#selectFilterDraftResultCount — the "Show XY Results" preview count), so the
 * preview can never disagree with what applying it produces. The same per-plan/per-unit predicates
 * also decide which lots stay matched under the applied filters (matchLotsByFilters — the map chip
 * badges and the map's lot coloring), so those agree with the listing too.
 *
 * @module nativeInteractiveSiteMap/services/filterResultsService
 */

import HousingType from 'enums/housingType';
import LotStatus from '../enums/lotStatus';
import matchesRange, { matchesCountRange } from './rangeFilterService';
import matchesHousingType from './housingTypeFilterService';
import matchesMoveInDate from './moveInDateFilterService';

const isEmptyRange = ({ minimum, maximum } = {}) => minimum == null && maximum == null;

/**
 * Lots whose status matches `chip`, keyed by both id spaces this feature matches against:
 * sitePlanLotId (string, SVG lot <path> elements) and lotId (integer, referenced by housePlans'
 * lotIds and housingUnits' lotId — only present once the lot-inventory fetch enriched the lot).
 *
 * @param {object[]} lots
 * @param {string} chip
 * @returns {{ sitePlanLotIds: Set<string>, lotIds: Set<number> }}
 */
export function matchLotIdsByChip(lots, chip) {
  const matchedLots = lots.filter((lot) => lot.status === chip);
  return {
    sitePlanLotIds: new Set(matchedLots.map((lot) => lot.sitePlanLotId)),
    lotIds: new Set(
      matchedLots.filter((lot) => lot.lotId != null).map((lot) => lot.lotId)
    ),
  };
}

/**
 * Whether a house plan matches every range/housing type filter. Move-in date is deliberately not
 * applied: only a housing unit has one (see moveInDateFilterService.js).
 *
 * @returns {boolean}
 */
export function matchesHousePlanFilters(plan, searchParameters) {
  return matchesRange(plan.price, searchParameters.price) &&
    matchesCountRange(plan.bedrooms, searchParameters.bedroom) &&
    matchesCountRange(plan.bathrooms, searchParameters.bathroom) &&
    matchesRange(plan.squareFeet, searchParameters.squareFeet) &&
    matchesHousingType(plan, searchParameters.housingType);
}

/**
 * Whether a housing unit matches every Filters modal filter, move-in date included.
 *
 * @returns {boolean}
 */
export function matchesHousingUnitFilters(unit, searchParameters) {
  return matchesRange(unit.price, searchParameters.price) &&
    matchesCountRange(unit.bedrooms, searchParameters.bedroom) &&
    matchesCountRange(unit.bathrooms, searchParameters.bathroom) &&
    matchesRange(unit.squareFeet, searchParameters.squareFeet) &&
    matchesHousingType(unit, searchParameters.housingType) &&
    matchesMoveInDate(unit.moveInDate, searchParameters.moveInDateRange);
}

/**
 * House plans buildable on at least one matched lot and matching every range/housing type filter
 * (see matchesHousePlanFilters). Each plan's `lotIds` is narrowed to the matched lots, and
 * `lotStatus` is read from the first of them (all share the chip's status by construction).
 *
 * @returns {object[]}
 */
export function filterHousePlans(housePlans, lotsById, matchedLotIds, searchParameters) {
  return housePlans
    .map((plan) => {
      const lotIds = plan.lotIds.filter((lotId) => matchedLotIds.lotIds.has(lotId));
      return {
        ...plan,
        lotIds,
        lotStatus: lotsById[lotIds[0]]?.status ?? null,
      };
    })
    .filter((plan) => plan.lotIds.length > 0 && matchesHousePlanFilters(plan, searchParameters));
}

/**
 * Housing units on a matched lot and matching every Filters modal filter (see
 * matchesHousingUnitFilters), enriched with the lot's display label and status for card rendering.
 *
 * @returns {object[]}
 */
export function filterHousingUnits(housingUnits, lotsById, matchedLotIds, searchParameters) {
  return housingUnits
    .filter((unit) => matchedLotIds.lotIds.has(unit.lotId) && matchesHousingUnitFilters(unit, searchParameters))
    .map((unit) => ({
      ...unit,
      lotDisplayLabel: lotsById[unit.lotId]?.displayLabel ?? null,
      lotStatus: lotsById[unit.lotId]?.status ?? null,
    }));
}

/**
 * How many of the Filters modal's six fields are applied — the number on the filter button's
 * badge. hasActiveFilters is defined on top of it, so the badge shows exactly when that's true.
 * Reads the raw `housingType`, so the Model chip picked on the map doesn't count.
 *
 * @returns {number}
 */
export function countActiveFilters(searchParameters) {
  return [
    !isEmptyRange(searchParameters.price),
    !isEmptyRange(searchParameters.bedroom),
    !isEmptyRange(searchParameters.bathroom),
    !isEmptyRange(searchParameters.squareFeet),
    searchParameters.moveInDateRange != null,
    searchParameters.housingType != null,
  ].filter(Boolean).length;
}

const RANGE_FILTER_FIELDS = ['price', 'bedroom', 'bathroom', 'squareFeet'];
const VALUE_FILTER_FIELDS = ['moveInDateRange', 'housingType'];

const isSameRange = (left = {}, right = {}) => left.minimum === right.minimum && left.maximum === right.maximum;

/**
 * Whether two values of the same Filters modal field are equal — ranges by their bounds, not by
 * reference, so re-picking the value a field already holds isn't a change.
 *
 * @param {string} field - a filterDraft key
 * @returns {boolean}
 */
export function isSameFilterFieldValue(field, left, right) {
  return RANGE_FILTER_FIELDS.includes(field) ? isSameRange(left, right) : left === right;
}

/**
 * Whether any of the Filters modal's six fields differs between the draft and what's applied —
 * i.e. closing the modal now would leave edits unapplied. Field values only: an edit that ended
 * back on the applied value isn't a change.
 *
 * @returns {boolean}
 */
export function hasPendingFilterChanges(filterDraft, searchParameters) {
  return [...RANGE_FILTER_FIELDS, ...VALUE_FILTER_FIELDS]
    .some((field) => !isSameFilterFieldValue(field, filterDraft[field], searchParameters[field]));
}

/**
 * Number of cards the listing sheet would show for `chip` — mirrors
 * services/listViewContentService.js's per-chip PANEL_CONTENT: Available counts both tabs (Floor
 * Plans + Quick Move-Ins), Model renders the union of plans and units, Coming Soon (and any other
 * chip) renders plans only, and Sold renders its own sold-home list, which the Filters modal
 * doesn't narrow.
 *
 * @param {string} chip
 * @param {{ housePlanCount: number, housingUnitCount: number, soldHomeCount: number }} counts
 * @returns {number}
 */
export function countResultCards(chip, { housePlanCount, housingUnitCount, soldHomeCount }) {
  if (chip === LotStatus.Available.toString() || chip === LotStatus.Model.toString()) {
    return housePlanCount + housingUnitCount;
  }

  if (chip === LotStatus.Sold.toString()) {
    return soldHomeCount;
  }

  return housePlanCount;
}

/**
 * Whether any of the Filters modal's own six fields is set to something other than "no filter".
 * `lotStatus` (the map chip bar) isn't one of them. Reads the raw `housingType`, not
 * getDisplayedHousingType: the Model chip picked on the map doesn't count as an active filter.
 *
 * @returns {boolean}
 */
export function hasActiveFilters(searchParameters) {
  return countActiveFilters(searchParameters) > 0;
}

/**
 * Lots (of every status) that still have something to show under the Filters modal filters —
 * the set the chip badge counts and the map's matched/unmatched coloring are derived from, so
 * both agree with the listing sheet. A lot is represented by its housing unit when it has one
 * (the same precedence services/lotRoutingService.js#resolveLotDestination and the lotMapSelection
 * panel use), otherwise by any of its house plans. Sold lots always pass, since the Filters modal
 * doesn't narrow the Sold listing (see countResultCards). With no filter active every lot passes
 * as-is, so nothing changes while the lot inventory is still loading.
 *
 * @param {object[]} lots
 * @param {object[]} housePlans
 * @param {object[]} housingUnits
 * @param {object} searchParameters
 * @returns {object[]}
 */
export function matchLotsByFilters(lots, housePlans, housingUnits, searchParameters) {
  if (!hasActiveFilters(searchParameters)) {
    return lots;
  }

  const matchingPlanIds = new Set(
    housePlans
      .filter((plan) => matchesHousePlanFilters(plan, searchParameters))
      .map((plan) => plan.planId)
  );
  const matchingUnitLotIds = new Set(
    housingUnits
      .filter((unit) => unit.lotId != null && matchesHousingUnitFilters(unit, searchParameters))
      .map((unit) => unit.lotId)
  );

  return lots.filter((lot) => {
    if (lot.status === LotStatus.Sold.toString()) {
      return true;
    }

    if (lot.housingUnitId != null) {
      return matchingUnitLotIds.has(lot.lotId);
    }

    return lot.housePlanIds.some((planId) => matchingPlanIds.has(planId));
  });
}

/**
 * The Housing type value the Filters modal field shows. With nothing explicitly chosen and the
 * Model chip active, results are narrowed to model homes anyway, so the field shows
 * 'modelHomes'; an explicit stored value is never overridden, since that's the one that actually
 * narrows the results.
 *
 * @returns {string|null}
 */
export function getDisplayedHousingType(searchParameters) {
  return searchParameters.housingType == null && searchParameters.lotStatus === LotStatus.Model.toString()
    ? HousingType.ModelHomes.toString()
    : searchParameters.housingType;
}
